import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setRewardsOpen } from 'store/uiSlice';
import { useMediaQuery } from '@mui/material';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { EighteenPlusIcon, LogoIcon } from 'components/icons';
import LanguageDropdown from 'components/languageDropdown/LanguageDropdown';
import LocalizedLink from 'components/common/LocalizedLink';
import styles from './footer.module.scss';

declare global {
  interface Window {
    anj_a325e852_475a_4a63_aa6f_290c32e7b745?: {
      init: () => void;
    };
  }
}

interface SocialItem {
  id: string | number;
  name?: string;
  socialLink: string;
  imageUrl: string;
  sortOrder: number;
}

interface FooterLink {
  key: string;
  label: string;
  to?: string;
  href?: string;
  action?: 'intercom' | 'rewards';
}

interface FooterSection {
  id: string;
  titleKey: string;
  title: string;
  links: FooterLink[];
}

const FOOTER_SECTIONS: FooterSection[] = [
  {
    id: 'casino',
    titleKey: 'common.casino',
    title: 'Casino',
    links: [
      { key: 'common.casinoLobby', label: 'Casino Lobby', to: '/' },
      { key: 'common.originals', label: 'Originals', to: '/casino/categories/originals' },
      { key: 'common.slots', label: 'Slots', to: '/casino/categories/slots' },
      { key: 'common.tableGames', label: 'Table Games', to: '/casino/categories/table_games' },
      { key: 'common.liveCasino', label: 'Live Casino', to: '/casino/categories/live_casino' },
      { key: 'common.gameShows', label: 'Game Shows', to: '/casino/categories/game_shows' },
      { key: 'common.newGames', label: 'New Games', to: '/casino/categories/new_games' },
    ],
  },
  {
    id: 'platform',
    titleKey: 'footer.sections.platform',
    title: 'Platform',
    links: [
      { key: 'common.rewards', label: 'Rewards', action: 'rewards' },
      { key: 'footer.links.affiliates', label: 'Affiliates', to: '/affiliate' },
    ],
  },
  {
    id: 'support',
    titleKey: 'footer.sections.support',
    title: 'Support',
    links: [
      { key: 'footer.links.liveSupport', label: 'Live Support', action: 'intercom' },
      {
        key: 'footer.links.reportProblem',
        label: 'Report a Problem',
        href: 'mailto:support@dgenta.com',
      },
      { key: 'footer.links.getHelp', label: 'Get Help', href: 'mailto:info@dgenta.com' },
    ],
  },
  {
    id: 'policy',
    titleKey: 'footer.sections.policy',
    title: 'Policy',
    links: [
      {
        key: 'footer.links.termsAndConditions',
        label: 'Terms and Conditions',
        to: '/policy/terms-and-conditions',
      },
      { key: 'footer.links.privacyPolicy', label: 'Privacy Policy', to: '/policy/privacy-policy' },
      { key: 'footer.links.amlPolicy', label: 'AML / KYC Policy', to: '/policy/aml-policy' },
      {
        key: 'common.responsibleGambling',
        label: 'Responsible Gambling',
        to: '/policy/responsible-gambling',
      },
      { key: 'footer.links.bonusPolicy', label: 'Bonus Policy', to: '/policy/bonus-policy' },
    ],
  },
];

const FooterDisclaimer: React.FC = () => {
  return (
    <div className={styles.disclaimerContainer}>
      <p className={styles.description}>
        <Trans
          i18nKey="footer.disclaimer1"
          components={{
            siteLink: <LocalizedLink to="/" className={styles.link} />,
            companyStrong: <strong />,
            emailLink: <a href="mailto:info@dgenta.com" className={styles.link} />,
          }}
        />
      </p>

      <p className={styles.description}>
        <Trans
          i18nKey="footer.disclaimer2"
          components={{
            licenseStrong: <strong />,
          }}
        />
      </p>
    </div>
  );
};

const Footer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const { config } = useAppSelector((state) => state.site);
  const location = useLocation();
  const isDesktop = useMediaQuery('(min-width:992px)');

  const visibleSocials: SocialItem[] = useMemo(() => {
    const rawSocials: SocialItem[] = (config?.socials as SocialItem[]) || [];
    return rawSocials
      .filter((social) => social.imageUrl)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [config?.socials]);

  const isSensitivePage =
    location.pathname.includes('/cashier') ||
    location.pathname.includes('/payment') ||
    location.pathname.includes('/deposit') ||
    location.pathname.includes('/withdraw') ||
    location.pathname.includes('/account-management');

  useEffect(() => {
    if (!isSensitivePage && window.anj_a325e852_475a_4a63_aa6f_290c32e7b745?.init) {
      const timer = setTimeout(() => {
        window.anj_a325e852_475a_4a63_aa6f_290c32e7b745?.init();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isSensitivePage, isDesktop]);

  const toggleSection = (section: string) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.brandSection}>
            <div className={styles.logo}>
              <LocalizedLink to="/">
                <LogoIcon width={120} height={38} fill="#ffffff" />
              </LocalizedLink>
            </div>

            <FooterDisclaimer />

            {!isDesktop && (
              <div className={styles.licenseAndSocials}>
                <div className={styles.socialColumn}>
                  <div className={styles.languageWrapper}>
                    <LanguageDropdown direction="up" />
                  </div>
                  <h3 className={styles.socialTitle}>
                    {t('footer.joinCommunity', 'Join our community')}
                  </h3>
                  <div className={styles.socialIcons}>
                    {visibleSocials?.map((social: SocialItem) => (
                      <a
                        key={social.id}
                        href={social.socialLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.socialIcon}
                        aria-label={social.name || 'social'}
                      >
                        <img src={social.imageUrl} alt={social.name || ''} width={28} height={28} />
                      </a>
                    ))}
                  </div>
                </div>

                {!isSensitivePage && (
                  <div className={styles.badgesWrapper}>
                    <LocalizedLink
                      to="/policy/responsible-gambling"
                      className={styles.responsibleBanner}
                    >
                      <span className={styles.licenseText}>
                        {t('common.responsibleGambling', 'Responsible Gambling')}
                      </span>
                      <div className={styles.eighteenPlusContainer}>
                        <EighteenPlusIcon width={35} height={35} />
                      </div>
                    </LocalizedLink>
                    <div className={styles.licenseBanner}>
                      <span className={styles.licenseText}>
                        {t('footer.licensedInAnjouan', 'Licensed in Anjouan')}
                      </span>
                      <div className={styles.licenseImages}>
                        <div
                          id="anj-a325e852-475a-4a63-aa6f-290c32e7b745"
                          data-anj-seal-id="a325e852-475a-4a63-aa6f-290c32e7b745"
                          data-anj-image-size="256"
                          data-anj-image-type="clean-large"
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.linksSection}>
            {FOOTER_SECTIONS.map((section) => (
              <div
                key={section.id}
                className={`${styles.column} ${activeSection === section.id ? styles.open : ''}`}
              >
                <h3 className={styles.columnTitle} onClick={() => toggleSection(section.id)}>
                  {t(section.titleKey, section.title)}
                  <span className={styles.arrow}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </h3>
                <div className={styles.accordionContent}>
                  <ul className={styles.columnList}>
                    {section.links.map((link, idx) => (
                      <li key={idx}>
                        {link.action === 'intercom' ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              showIntercom();
                            }}
                          >
                            {t(link.key, link.label)}
                          </a>
                        ) : link.action === 'rewards' ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              dispatch(setRewardsOpen(true));
                            }}
                          >
                            {t(link.key, link.label)}
                          </a>
                        ) : link.to ? (
                          <LocalizedLink to={link.to}>{t(link.key, link.label)}</LocalizedLink>
                        ) : (
                          <a href={link.href}>{t(link.key, link.label)}</a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
        {isDesktop && (
          <div className={styles.licenseAndSocialsDesktop}>
            <div className={styles.socialColumn}>
              <div className={styles.languageWrapper}>
                <LanguageDropdown direction="up" />
              </div>
              <h3 className={styles.socialTitle}>
                {t('footer.joinCommunity', 'Join our community')}
              </h3>
              <div className={styles.socialIcons}>
                {visibleSocials?.map((social: SocialItem) => (
                  <a
                    key={social.id}
                    href={social.socialLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialIcon}
                    aria-label={social.name || 'social'}
                  >
                    <img src={social.imageUrl} alt={social.name || ''} width={28} height={28} />
                  </a>
                ))}
              </div>
            </div>

            {!isSensitivePage && (
              <div className={styles.badgesWrapper}>
                <LocalizedLink
                  to="/policy/responsible-gambling"
                  className={styles.responsibleBanner}
                >
                  <span className={styles.licenseText}>
                    {t('common.responsibleGambling', 'Responsible Gambling')}
                  </span>
                  <div className={styles.eighteenPlusContainer}>
                    <EighteenPlusIcon width={35} height={35} />
                  </div>
                </LocalizedLink>
                <div className={styles.licenseBanner}>
                  <span className={styles.licenseText}>
                    {t('footer.licensedInAnjouan', 'Licensed in Anjouan')}
                  </span>
                  <div className={styles.licenseImages}>
                    <div
                      id="anj-a325e852-475a-4a63-aa6f-290c32e7b745"
                      data-anj-seal-id="a325e852-475a-4a63-aa6f-290c32e7b745"
                      data-anj-image-size="256"
                      data-anj-image-type="clean-large"
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className={styles.bottomSection}>
          <div className={styles.copyright}>
            Copyright © 2026 <LocalizedLink to="/">Dgenta.com</LocalizedLink> |{' '}
            {t('footer.allRightsReserved', 'All Rights Reserved')}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
